import { Injectable, Optional } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MatomoTracker } from 'ngx-matomo-client';
import { Observable, isObservable, of, tap } from 'rxjs';
import { deepEqual } from '../deep-equal';
import { ClickCallbackLink } from '../link';
import { ActionClass } from '../state';
import { Notification } from './model/notification';
import { isNotificationActionLink } from './model/notification-action-link';
import { NotificationOptions } from './model/notification-options';
import { NotificationStateModel } from './model/notification-state-model';
import { PartialNotification } from './model/partial-notification';
import { DEFAULT_NOTIFICATION_PRIORIZATION_LEVELS } from './notification-priorization';
import {
  ClearNotifications,
  DismissNotification,
  ShowErrorNotification,
  ShowInfoNotification,
  ShowRefreshNotification,
  ShowSuccessNotification,
  ShowWarningNotification
} from './notification.actions';

@State<NotificationStateModel>({
  name: 'notification',
  defaults: {
    notifications: []
  }
})
@Injectable()
export class NotificationState {
  constructor(@Optional() private readonly matomoTracker: MatomoTracker) {}

  @Selector()
  static notifications(state: NotificationStateModel): Notification[] {
    return state.notifications;
  }

  @Selector()
  static errorNotifications(state: NotificationStateModel): Notification[] {
    return state.notifications.filter(n => n.notificationType === 'error');
  }

  @Selector()
  static hasNotifications(state: NotificationStateModel): boolean {
    return state.notifications.length > 0;
  }

  @Selector()
  static hasRefreshNotifications(state: NotificationStateModel): boolean {
    return state.notifications.filter(n => n.notificationType === 'refresh').length > 0;
  }

  @Action(ShowSuccessNotification)
  @Action(ShowInfoNotification)
  @Action(ShowRefreshNotification)
  @Action(ShowWarningNotification)
  @Action(ShowErrorNotification)
  show(ctx: StateContext<NotificationStateModel>, action: PartialNotification): void {
    const notifications = ctx.getState().notifications;
    const notification = this.buildNotification(ctx, action);
    const keepExisting = notification.options.keepExisting;
    const priorizationLevel = notification.options.priorizationLevel ?? DEFAULT_NOTIFICATION_PRIORIZATION_LEVELS[notification.notificationType];
    const higherPrioritizedNotifications = notifications.filter(n => (n.options.priorizationLevel ?? 0) > priorizationLevel);

    ctx.patchState({
      notifications:
        higherPrioritizedNotifications.length > 0 && !keepExisting
          ? higherPrioritizedNotifications
          : [...notifications.filter(n => keepExisting && this.differByNotificationTypeOrMessage(n, notification)), notification]
    });

    if (this.matomoTracker) {
      const eventName = this.getEventNameForType(notification.notificationType);
      this.matomoTracker.trackEvent('Notifications', eventName, notification.message.key);
    }
  }

  private buildNotification(ctx: StateContext<NotificationStateModel>, partial: PartialNotification): Notification {
    const inputParams = partial.message.params ?? {};
    const keys = Object.keys(inputParams);
    const params: Record<string, any> = {};
    const spinnerInclusiveActions: NotificationOptions['spinnerInclusiveActions'] = [];

    for (const key of keys) {
      const value = inputParams[key];
      if (isNotificationActionLink(value)) {
        const clickCallbackLink: ClickCallbackLink = {
          class: value.class,
          onClick: () => ctx.dispatch(value.action),
          id: '3c29d4cd-a323-42f3-b5b6-0973962eb48f-' + key
        };
        params[key] = clickCallbackLink;
        spinnerInclusiveActions.push(value.action.constructor as ActionClass);
      } else {
        params[key] = value;
      }
    }

    return {
      id: Symbol('notification id'),
      notificationType: partial.notificationType,
      message: { ...partial.message, params },
      options: {
        keepExisting: false,
        tags: [],
        autoDismiss: false,
        onDismiss: () => of(null),
        priorizationLevel: null,
        ...partial.options,
        spinnerInclusiveActions: [...spinnerInclusiveActions, ...(partial.options?.spinnerInclusiveActions ?? [])]
      }
    };
  }

  private differByNotificationTypeOrMessage(n1: Notification, n2: Notification): boolean {
    return n1.notificationType !== n2.notificationType || !deepEqual(n1.message, n2.message);
  }

  @Action(DismissNotification)
  dismiss(ctx: StateContext<NotificationStateModel>, { id }: DismissNotification): Observable<unknown> {
    const notifications = ctx.getState().notifications;
    const dismissedNotification = notifications.find(n => n.id === id);

    ctx.patchState({
      notifications: notifications.filter(n => n.id !== id)
    });

    if (dismissedNotification && this.matomoTracker) {
      this.matomoTracker.trackEvent('Notifications', 'Dismiss_Banner', dismissedNotification.notificationType);
    }

    const onDissmissHandler = dismissedNotification?.options.onDismiss;
    if (typeof onDissmissHandler === 'function') {
      const onDismissHandlerResult = onDissmissHandler();
      if (isObservable(onDismissHandlerResult)) {
        return onDismissHandlerResult;
      } else {
        return of(null);
      }
    } else {
      return ctx.dispatch(onDissmissHandler);
    }
  }

  @Action(ClearNotifications)
  clear(ctx: StateContext<NotificationStateModel>, { tags }: ClearNotifications): Observable<unknown> {
    const notifications = ctx.getState().notifications;
    const remainingNotifications: Notification[] = tags.length === 0 ? [] : notifications.filter(n => this.isDisjoint(n.options.tags, tags));
    const dismissActions: any[] = [];
    notifications
      .filter(n => !remainingNotifications.includes(n))
      .forEach(n => {
        if (n.options.autoDismiss) dismissActions.push(new DismissNotification(n.id));
      });
    return ctx.dispatch(dismissActions).pipe(tap(() => ctx.patchState({ notifications: remainingNotifications })));
  }

  private isDisjoint<T>(array1: T[], array2: T[]): boolean {
    return array1.every(item1 => !array2.includes(item1));
  }

  private getEventNameForType(type: string): string {
    switch (type) {
      case 'success':
        return 'Trigger_SuccessBanner';
      case 'info':
        return 'Trigger_InfoBanner';
      case 'warning':
        return 'Trigger_WarningBanner';
      case 'error':
        return 'Trigger_ErrorBanner';
      case 'refresh':
        return 'Trigger_RefreshBanner';
      default:
        return `Trigger_${type}Banner`;
    }
  }
}
